import { Component, Input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
	IPostalCode,
	ZkPostalCodeService,
	ZkZipCodePipe,
} from '@zenklub/ng-ui';
import { ZkIOption } from '@zenklub/ng-ui/component/input/dropdown/dropdown.interface';

import { IRegion } from '../../../interfaces/location.interface';
import { LocationService } from '../../../services/location.service';

@Component({
	selector: 'app-billing-address',
	templateUrl: './billing-address.component.html',
	styleUrls: ['./billing-address.component.scss'],
	providers: [ZkZipCodePipe],
})
export class BillingAddressComponent implements OnInit {
	@Input() formParams: FormGroup;
	@Input() showTitle = true;

	states: ZkIOption[] = [];
	brazilRegions: IRegion[] = [];
	showLoading: boolean;
	constructor(
		private translate: TranslateService,
		private zipCodePipe: ZkZipCodePipe,
		public postalCode: ZkPostalCodeService,
		public locationService: LocationService
	) {}

	ngOnInit(): void {
		this.getStates();
	}

	isFieldInvalid(formGroup: FormGroup, fieldName: string): boolean {
		const field = formGroup.get(fieldName);
		if (field) {
			return field.invalid && (field.dirty || field.touched);
		}
		return false;
	}

	getLabelDescription(fieldName: string) {
		const translatePath = 'MY_ACCOUNT.FINANCIAL.BILLING_ADDRESS';
		return this.translate.instant(
			`${translatePath}.${fieldName.toUpperCase()}`
		);
	}

	async zipCodeChange(event: KeyboardEvent): Promise<void> {
		if (this.isIgnoredKey(event.key)) return;

		event.preventDefault();

		return new Promise(async (resolve) => {
			const target = event.target as HTMLInputElement;
			let { value } = target;

			if (event.key === 'Backspace') value = value.slice(0, -1);
			if (event.key === 'Delete') value = '';

			if (this.formParams.get('liveOutsideBrazil')?.value) {
				target.value = this.removeNonNumber(`${value}${event.key}`);
			} else {
				target.value = this.applyMaskToPostalCode(`${value}${event.key}`);
			}
			this.formParams.patchValue({ postalCode: target.value });
			resolve();
		});
	}

	isIgnoredKey(key: string): boolean {
		const IGNORED_KEYS = [
			'Alt',
			'ArrowDown',
			'ArrowLeft',
			'ArrowRight',
			'ArrowUp',
			'Control',
			'End',
			'Home',
			'Shift',
			'Tab',
			'Esc',
		];

		return IGNORED_KEYS.includes(key);
	}

	async onZipCodeChange() {
		const zipcode = this.formParams.get('postalCode').value;
		if (!this.postalCode.isValid(zipcode)) {
			return;
		}
		this.showLoading = true;
		const postalCode = await this.getPostalCode(zipcode);
		if (!postalCode || postalCode.notFound) {
			this.formParams.patchValue({
				address: '',
				neighborhood: '',
				city: '',
				addressNumber: '',
			});
			this.formParams.updateValueAndValidity();
			this.showLoading = false;
			return;
		}
		const state =
			this.brazilRegions.find((region) => region.code === postalCode.state)
				?.name || '';
		this.formParams.patchValue({
			address: postalCode.address,
			neighborhood: postalCode.neighborhood,
			city: postalCode.city,
			state: state,
			addressNumber: '',
		});
		this.showLoading = false;
	}

	getPostalCodeStatus() {
		if (this.showLoading) {
			return 'loading';
		}
		if (this.isFieldInvalid(this.formParams, 'postalCode')) {
			return 'error';
		}
		return 'default';
	}

	applyMaskToPostalCode(postalCode: string): string {
		const val = this.removeNonNumber(postalCode);
		return this.zipCodePipe.transform(val, 'BRAZIL' as any);
	}

	removeNonNumber(value: string): string {
		return (value || '').replace(/\D/g, '');
	}

	private getPostalCode(zipCode: string): Promise<IPostalCode> {
		return new Promise<IPostalCode>((resolve) => {
			this.postalCode.get(zipCode).subscribe((postalCode) => {
				resolve(postalCode);
			});
		});
	}

	private getStates() {
		this.locationService.getCountryRegions(1).subscribe((res) => {
			this.brazilRegions = res;
			this.states = res.map(({ name }) => ({ label: name, value: name }));
		});
	}
}
